import React, { useState } from 'react';

/**
 * A claim's history: every approval stage it passed through, grouped into the
 * versions created by each rejection.
 *
 * The data already existed - both claim-by-id endpoints return `approvalChain`
 * from the tamper-evident hash chain - but nothing rendered it, so a claim that
 * had been returned twice looked identical to one submitted cleanly and the
 * reason for a rejection was invisible once the claimant had resubmitted.
 *
 * Versions are DERIVED rather than stored: a rejection or return ends a version,
 * so the entries before the first rejection are v1, those up to the second are
 * v2, and so on. That needs no schema change and cannot drift from the chain.
 */

/** Stages that close a version - the claim goes back to the claimant/admin. */
const REJECTION_STAGES = new Set([
  'auditor_returned',
  'auditor_rejected',
  'regional_director_rejected',
  'global_director_rejected',
  'rejected',
]);

const STAGE_LABELS = {
  created: 'Created',
  submitted: 'Submitted',
  submitted_for_review: 'Submitted for review',
  included_in_report: 'Included in monthly report',
  auditor_approved: 'Audited',
  auditor_returned: 'Returned for revision by auditor',
  auditor_rejected: 'Rejected by auditor',
  regional_director_reviewed: 'Reviewed by Regional Director',
  regional_director_approved: 'Approved by Regional Director',
  regional_director_rejected: 'Rejected by Regional Director',
  global_director_reviewed: 'Reviewed by Global/Finance Director',
  global_director_approved: 'Approved by Global/Finance Director',
  global_director_rejected: 'Rejected by Global/Finance Director',
  claim_flagged: 'Flagged for follow-up',
  clarification_asked: 'Question asked',
  clarification_closed: 'Question closed',
  rejected: 'Rejected',
  resubmitted: 'Resubmitted',
  modified: 'Modified',
};

const stageLabel = (stage) => STAGE_LABELS[stage] || String(stage || '').replace(/_/g, ' ');

const stageTone = (stage) => {
  if (REJECTION_STAGES.has(stage)) return { dot: 'bg-red-500', text: 'text-red-800' };
  if (String(stage).includes('approved') || String(stage).includes('reviewed')) {
    return { dot: 'bg-emerald-500', text: 'text-emerald-800' };
  }
  if (stage === 'claim_flagged') return { dot: 'bg-amber-500', text: 'text-amber-800' };
  return { dot: 'bg-gray-400', text: 'text-gray-700' };
};

const fmt = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: 'numeric', minute: '2-digit',
      });
};

/** Split the chain into versions, each ending at a rejection. */
function toVersions(chain) {
  const versions = [];
  let current = [];

  for (const entry of chain) {
    current.push(entry);
    if (REJECTION_STAGES.has(entry.stage)) {
      versions.push(current);
      current = [];
    }
  }
  // Whatever follows the last rejection is the version in play now.
  if (current.length > 0) versions.push(current);
  return versions;
}

const ClaimHistoryPanel = ({ approvalChain = [], editHistory = [], defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  const chain = Array.isArray(approvalChain) ? approvalChain : [];
  const versions = toVersions(chain);
  const rejectionCount = chain.filter(e => REJECTION_STAGES.has(e.stage)).length;

  if (chain.length === 0 && (editHistory || []).length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <h3 className="text-lg font-semibold text-gray-800">History</h3>
        <p className="text-sm text-gray-500 mt-1">
          No approval history recorded for this claim yet.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex flex-wrap items-center justify-between gap-3 text-left"
      >
        <div>
          <h3 className="text-lg font-semibold text-gray-800">History</h3>
          <p className="text-sm text-gray-600">
            {versions.length === 1
              ? `${chain.length} event${chain.length === 1 ? '' : 's'}`
              : `${versions.length} versions · ${chain.length} events`}
            {rejectionCount > 0 && (
              <span className="text-red-700 font-medium">
                {' '}· returned {rejectionCount} time{rejectionCount === 1 ? '' : 's'}
              </span>
            )}
          </p>
        </div>
        <span className="text-sm text-stanford-red font-medium">
          {open ? 'Hide' : 'Show'} history
        </span>
      </button>

      {open && (
        <div className="mt-5 space-y-5">
          {versions.map((entries, i) => {
            const closedBy = entries.find(e => REJECTION_STAGES.has(e.stage));
            const isCurrent = i === versions.length - 1 && !closedBy;
            return (
              <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className={`px-3 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm ${
                  closedBy ? 'bg-red-50' : isCurrent ? 'bg-emerald-50' : 'bg-gray-50'
                }`}>
                  <span className="font-semibold text-gray-800">
                    Version {i + 1}
                    {isCurrent && <span className="font-normal text-emerald-700"> · current</span>}
                  </span>
                  {closedBy && (
                    <span className="text-red-800 text-xs">
                      Returned {fmt(closedBy.performedAt)}
                    </span>
                  )}
                </div>

                <ol className="divide-y divide-gray-100">
                  {entries.map((e, j) => {
                    const tone = stageTone(e.stage);
                    return (
                      <li key={j} className="px-3 sm:px-4 py-3 flex gap-3">
                        <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${tone.dot}`} />
                        <div className="min-w-0">
                          <p className={`text-sm font-medium ${tone.text}`}>
                            {stageLabel(e.stage)}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {e.performer?.name || 'System'}
                            {e.performer?.role ? ` (${String(e.performer.role).replace(/_/g, ' ')})` : ''}
                            {' · '}{fmt(e.performedAt)}
                          </p>
                          {/* The reason a claim came back is the single most
                              useful thing here, so it is never collapsed. */}
                          {e.comments && (
                            <p className={`text-sm mt-1.5 whitespace-pre-wrap break-words rounded px-2 py-1 ${
                              REJECTION_STAGES.has(e.stage)
                                ? 'bg-red-50 text-red-900'
                                : 'bg-gray-50 text-gray-700'
                            }`}>
                              {e.comments}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            );
          })}

          {/* Field-level edits, where the claim type records them. Separate from
              the approval chain because these are changes to the claim's
              contents rather than movements through the workflow. */}
          {(editHistory || []).length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-3 sm:px-4 py-2 bg-gray-50 text-sm font-semibold text-gray-800">
                Field edits ({editHistory.length})
              </div>
              <ul className="divide-y divide-gray-100">
                {editHistory.map((edit, i) => (
                  <li key={edit.id || i} className="px-3 sm:px-4 py-2 text-sm break-words">
                    <span className="text-gray-800">
                      {edit.fieldName || edit.field || 'Change'}
                    </span>
                    {edit.oldValue !== undefined && (
                      <span className="text-gray-500">
                        {' '}: {String(edit.oldValue) || '-'} → {String(edit.newValue) || '-'}
                      </span>
                    )}
                    <span className="block text-xs text-gray-500 mt-0.5">
                      {edit.editedByName || edit.editedBy || 'Unknown'} · {fmt(edit.editedAt || edit.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClaimHistoryPanel;
